import React from "react";
import { Sparkles, Network, ShieldCheck, ExternalLink } from "lucide-react";
import { ConceptMap } from "./ConceptMap";

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const PanelSection = ({ icon: Icon, title, children }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-2 text-primary">
      <Icon strokeWidth={1.5} className="w-4 h-4" />
      <h3 className="text-xs tracking-[0.18em] uppercase font-medium">{title}</h3>
    </div>
    {children}
  </section>
);

export const AnalysisPanel = ({ doc, selected, onSelect }) => (
  <div className="flex flex-col gap-9">
    <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground">Analisi Concettuale</p>

    <PanelSection icon={Sparkles} title="Insight Summary">
      <p data-testid="insight-summary" className="text-sm leading-relaxed text-foreground/90">
        {doc.insight_summary}
      </p>
    </PanelSection>

    <PanelSection icon={Network} title="Key Concepts">
      <div className="rounded-xl border border-border bg-secondary/40 h-56 p-2">
        <ConceptMap
          concepts={doc.concepts}
          title={doc.site_name || "Topic"}
          selectedName={selected?.name}
          onSelect={onSelect}
          variant="mini"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {doc.concepts.map((c) => {
          const active = selected?.name?.toLowerCase() === c.name.toLowerCase();
          return (
            <button
              key={c.name}
              data-testid="concept-chip"
              onClick={() => onSelect?.(c)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                active
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              }`}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      {selected && (
        <div data-testid="concept-detail" className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm font-medium text-primary mb-1">{selected.name}</p>
          <p className="text-sm text-foreground/80 leading-relaxed">{selected.summary}</p>
        </div>
      )}
    </PanelSection>

    {doc.causal_relations?.length > 0 && (
      <PanelSection icon={Network} title="Causal Links">
        <ul className="space-y-2">
          {doc.causal_relations.map((r, i) => (
            <li key={`${i}-${r.cause}`} className="text-sm text-foreground/80 flex items-center gap-2 flex-wrap">
              <span>{r.cause}</span>
              <span className="text-primary">→</span>
              <span>{r.effect}</span>
            </li>
          ))}
        </ul>
      </PanelSection>
    )}

    <PanelSection icon={ShieldCheck} title="Verifica Fonti">
      <ul className="space-y-2">
        {doc.sources.map((s, i) => (
          <li key={`${i}-${s.url}`}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="verify-source"
              className="group flex items-center gap-3 rounded-lg border border-border hover:border-primary/40 p-3 transition-colors"
            >
              <img
                src={`https://www.google.com/s2/favicons?domain=${hostOf(s.url)}&sz=64`}
                alt=""
                className="w-5 h-5 rounded shrink-0"
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground truncate">{hostOf(s.url)}</p>
              </div>
              <ExternalLink strokeWidth={1.5} className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </PanelSection>
  </div>
);
